import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Star, Trash2, Loader2 } from 'lucide-react'
import { EDITOR_CANVAS_STYLE } from '@/lib/emailEditorConfig'
import {
  useSignatures,
  useCreateSignature,
  useUpdateSignature,
  useDeleteSignature,
} from '@/hooks/useApi'
import type { SignatureData } from '@/hooks/useApi'
import { SignatureEditor } from '@/components/SignatureEditor'

export function Signatures() {
  const { t } = useTranslation('settings')
  const { data: signatures, isLoading: loadingSigs } = useSignatures()
  const createSignature = useCreateSignature()
  const updateSignature = useUpdateSignature()
  const deleteSignature = useDeleteSignature()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [formName, setFormName] = useState('')
  const [formHtml, setFormHtml] = useState('')
  // Immagini ancora in upload/import: salvare ora lascerebbe link esterni nella firma (#263).
  const [imagesBusy, setImagesBusy] = useState(false)

  const startCreate = () => {
    setIsCreating(true)
    setEditingId(null)
    setFormName('')
    setFormHtml('')
  }

  const startEdit = (sig: SignatureData) => {
    setIsCreating(false)
    setEditingId(sig.id)
    setFormName(sig.name)
    setFormHtml(sig.body_html)
  }

  const cancelEdit = () => {
    setIsCreating(false)
    setEditingId(null)
  }

  const handleSave = () => {
    if (!formName.trim() || !formHtml.trim()) return
    if (isCreating) {
      createSignature.mutate(
        { name: formName, body_html: formHtml },
        { onSuccess: () => cancelEdit() },
      )
    } else if (editingId) {
      updateSignature.mutate(
        { id: editingId, name: formName, body_html: formHtml },
        { onSuccess: () => cancelEdit() },
      )
    }
  }

  return (
    <div className="container mx-auto max-w-4xl py-8 px-4">
      <h1 className="text-3xl font-bold mb-2">{t('signatures.title')}</h1>
      <p className="text-muted-foreground mb-8">{t('signatures.subtitle')}</p>

      <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('signatures.cardTitle')}</CardTitle>
                <CardDescription>{t('signatures.cardDescription')}</CardDescription>
              </div>
              <Button onClick={startCreate} disabled={isCreating}>
                <Plus className="h-4 w-4 mr-2" />
                {t('signatures.new')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create / Edit form */}
            {(isCreating || editingId) && (
              <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
                <div className="space-y-2">
                  <Label>{t('signatures.nameLabel')}</Label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder={t('signatures.namePlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('signatures.signatureLabel')}</Label>
                  <SignatureEditor
                    // Un editor per firma: un upload della firma A non finisce nella B.
                    key={editingId ?? 'new'}
                    value={formHtml}
                    onChange={setFormHtml}
                    onBusyChange={setImagesBusy}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {t('signatures.formattingNote')}
                  </p>
                </div>
                {formHtml && (
                  <div className="border rounded p-3 text-sm bg-white dark:bg-zinc-900">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{t('signatures.previewLabel')}</p>
                    <div
                      className="email-preview"
                      style={EDITOR_CANVAS_STYLE}
                      dangerouslySetInnerHTML={{ __html: formHtml }}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!formName.trim() || !formHtml.trim() || imagesBusy || createSignature.isPending || updateSignature.isPending}
                  >
                    {(createSignature.isPending || updateSignature.isPending) && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    {isCreating ? t('signatures.create') : t('signatures.save')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancelEdit}>
                    {t('signatures.cancel')}
                  </Button>
                </div>
              </div>
            )}

            {/* Signatures list */}
            {loadingSigs ? (
              <Skeleton className="h-20 w-full" />
            ) : signatures && signatures.length > 0 ? (
              signatures.map((sig) => (
                <div
                  key={sig.id}
                  className={`border rounded-lg p-4 ${editingId === sig.id ? 'ring-2 ring-primary' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{sig.name}</h4>
                      {sig.is_default && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Star className="h-3 w-3" /> {t('signatures.default')}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!sig.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => updateSignature.mutate({ id: sig.id, is_default: true })}
                        >
                          <Star className="h-3.5 w-3.5 mr-1" />
                          {t('signatures.makeDefault')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => startEdit(sig)}
                      >
                        {t('signatures.edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-destructive"
                        onClick={() => deleteSignature.mutate(sig.id)}
                        disabled={deleteSignature.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div
                    className="email-preview border-t pt-2"
                    style={EDITOR_CANVAS_STYLE}
                    dangerouslySetInnerHTML={{ __html: sig.body_html }}
                  />
                </div>
              ))
            ) : !isCreating ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t('signatures.empty')}
              </p>
            ) : null}
          </CardContent>
        </Card>
    </div>
  )
}
